import { tsAddAnimal } from './ValidSamplesState'
import { delay } from '../../../Functions/DelayFn'
import { tsWhoAmI } from '../../../Typescript/API_Response'
import {
  validPatternSampleID,
  validServerSampleID,
} from '../../../APIs/Hooks/SampleHooks'
import { timeoutDuration } from './Constants'
import { manageSampleFocus } from './Functions/manageSampleFocus'

/**
 * Flattens and returns all sample IDs currently entered across all animals.
 * Used to quickly check for duplicates before hitting the server.
 */
function combineSampleIDs(animals: tsAddAnimal) {
  const ids: string[] = []
  Object.values(animals).forEach((samples) => {
    samples.forEach((s) => {
      if (s?.sampleID) {
        ids.push(s.sampleID)
      }
    })
  })
  return ids
}
/**
 * Handle Enter/Tab on a Sample ID input.
 *
 * Validations performed in order:
 * 1) Debounce rapid scans (boolean qty only) using timeoutDuration.
 * 2) Non-empty value.
 * 3) Pattern check (client-side): XX00000 via validPatternSampleID.
 * 4) Duplicate within the current page state.
 * 5) Server check to ensure the ID is not already assigned to another animal.
 *
 * On success, marks the current row valid and advances focus to the next animal input.
 */
export function onScanFn(
  event: any,
  whoAmI: tsWhoAmI | undefined,
  lastScan: number,
  setLastScan: any,
  setShowTimeout: any,
  animals: tsAddAnimal,
  setAnimals: any,
  playScanSound: any,
  currentCursor: number,
  sampleIndex: number,
  qty: 'boolean' | 'multiple',
) {
  const timeNow = Date.now()
  const val = event.target.value
  const uai = event.target.name
  // Only react on Enter/Tab key presses from the input
  if (['Enter', 'NumpadEnter', 'Tab'].includes(event.key)) {
    playScanSound(true)
    // Debounce: prevent rapid-fire scans on boolean flows (skip in local dev for easier testing)
    if (
      process.env.REACT_APP_STAGE !== 'local' &&
      timeNow - lastScan < timeoutDuration &&
      qty === 'boolean'
    ) {
      setShowTimeout(timeNow)
      // Clear the just-entered value to make the debouncing visible to users
      setAnimals((a: any) => {
        return {
          ...a,
          [uai]: [{ ...a[uai]?.[0], sampleID: '' }],
        }
      })
      setTimeout(
        () => {
          setShowTimeout(null)
        },
        timeoutDuration - (timeNow - lastScan),
      )
    } else {
      setLastScan(timeNow)
      // Small delay allows barcode scanners that paste progressively to finish
      delay(500).then(async () => {
        if (!val) {
          setAnimals((a: any) => {
            const newUAIsamples = a[uai]
            newUAIsamples.splice(sampleIndex, 1, {
              ...a[uai][sampleIndex],
              valid: false,
              message: 'ID must contain a value or not be collected',
            })
            return {
              ...a,
              [uai]: newUAIsamples,
            }
          })
          manageSampleFocus(currentCursor)
        } else if (!validPatternSampleID(val)) {
          setAnimals((a: any) => {
            const newUAIsamples = a[uai]
            newUAIsamples.splice(sampleIndex, 1, {
              ...a[uai][sampleIndex],
              valid: false,
              message: 'ID does not match XX00000 pattern',
            })
            return {
              ...a,
              [uai]: newUAIsamples,
            }
          })
          manageSampleFocus(currentCursor)
        } else if (
          // not duplicate
          val &&
          combineSampleIDs(animals).filter((s) => s === val).length > 1
        ) {
          setAnimals((a: any) => {
            const newUAIsamples = a[uai]
            newUAIsamples.splice(sampleIndex, 1, {
              ...a[uai][sampleIndex],
              valid: false,
              message: 'You just assigned this ID to another animal',
            })
            return {
              ...a,
              [uai]: newUAIsamples,
            }
          })
          manageSampleFocus(currentCursor)
        } else {
          const serverCheck = await validServerSampleID(whoAmI, val)
          if (serverCheck.UAI !== -1) {
            setAnimals((a: any) => {
              const newUAIsamples = a[uai]
              newUAIsamples.splice(sampleIndex, 1, {
                ...a[uai][sampleIndex],
                valid: false,
                message: `This ID is already assigned to the animal with UAI ${serverCheck.UAI}`,
              })
              return {
                ...a,
                [uai]: newUAIsamples,
              }
            })
            manageSampleFocus(currentCursor)
          } else {
            setAnimals((a: any) => {
              const newUAIsamples = a[uai]
              newUAIsamples.splice(sampleIndex, 1, {
                ...a[uai][sampleIndex],
                valid: true,
                message: '',
              })
              return {
                ...a,
                [uai]: newUAIsamples,
              }
            })
            manageSampleFocus(currentCursor + 1)
          }
        }
      })
    }
  }
}
